import { Button } from '@/components/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
	Sheet,
	SheetContent,
	SheetDescription,
	SheetFooter,
	SheetHeader,
	SheetTitle,
	SheetTrigger
} from '@/components/ui/sheet';
import { useIsDesktopSize } from '@/hooks/useIsDesktopSize';
import { cn } from '@/lib/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useLoaderData } from 'react-router';
import {
	CreateMeasurementGroupFormSchema,
	createMeasurementGroupSchema,
	MeasurementGroupTypes,
	MeasurementGroupTypesEnum
} from './create-measurement-group-schema';
import Spinner from '@/components/spinner/Spinner';
import { useMutation } from '@tanstack/react-query';
import { CreateMeasurementGroupRequest } from '../types';
import { saveMeasurementGroup } from '../constants';
import { toast } from 'sonner';
import { getUIErrorMessage } from '@/utils/errors';
import { RHFAutoComplete } from '@/components/auto-complete/RHFAutoComplete';
import { MeasurementGroupListLoaderResponse, MgLoad, MgPlant } from '../list/types';

export type CreateMeasurementGroupFormProps = {
	isOpen: boolean;
	handleToggleForm: () => void;
};

export const CreateMeasurementGroupForm = (props: CreateMeasurementGroupFormProps) => {
	const { isOpen, handleToggleForm } = props;

	const { mgLineList, mgLoadList, mgPlantList, minifiedProcessObjects } =
		useLoaderData() as MeasurementGroupListLoaderResponse;

	const [showConfirmation, setShowConfirmation] = useState(false);
	const [error, setError] = useState('');

	const createmeasurementGroupForm = useForm<CreateMeasurementGroupFormSchema>({
		resolver: zodResolver(createMeasurementGroupSchema),
		defaultValues: {
			name: '',
			type: undefined,
			objectList: [],
			processObjectList: []
		}
	});

	const { isDesktop } = useIsDesktopSize();

	const { isPending: isSaving, mutate: createMeasurementGroup } = useMutation({
		mutationFn: saveMeasurementGroup,
		mutationKey: ['measurement-groups'],
		onSuccess: () => {
			createmeasurementGroupForm.reset();
			handleToggleForm();
			setShowConfirmation(false);
			toast('Measurement group saved successfully');
		},
		onError: error => {
			setError(getUIErrorMessage(error));
		}
	});

	const handleSaveMeasurementGroup = () => {
		const submitForm = createmeasurementGroupForm.handleSubmit(values => {
			const payload: CreateMeasurementGroupRequest = {
				name: values.name,
				type: values.type,
				processObjectList: values.processObjectList.map(po => po.value),
				objectList: (values.objectList ?? []).map(ob => ob.value)
			};
			createMeasurementGroup(payload);
		});
		submitForm();
	};

	const handleCloseForm = () => {
		handleToggleForm();
		setShowConfirmation(false);
		setError('');
	};

	const getPlantName = (plant: MgPlant) => {
		const { plantName, shortName, nameplateCapacity, city, state } = plant;
		const name = shortName ? shortName : plantName;
		return `${name} - ${nameplateCapacity} MW - ${city}, ${state}`;
	};

	const getLoadName = (load: MgLoad) => {
		const { loadName, shortName, loadCapacity, city, state } = load;
		const name = shortName ? shortName : loadName;
		return `${name} - ${loadCapacity} MW - ${city}, ${state}`;
	};

	return (
		<Sheet open={isOpen} onOpenChange={handleCloseForm}>
			<SheetTrigger>
				<Button asChild variant="outline" onClick={handleToggleForm} className="flex justify-start w-fit">
					<span>
						<Plus />
						New measurement group
					</span>
				</Button>
			</SheetTrigger>
			<SheetContent
				side={!isDesktop ? 'bottom' : 'right'}
				className={cn('h-full p-5 overflow-auto', { 'sm:max-w-xl ': isDesktop, 'w-full h-5/6': !isDesktop })}
			>
				<SheetHeader>
					<SheetTitle>Create a new measurement group</SheetTitle>
					<SheetDescription>Fill in the details. Click save when you're done.</SheetDescription>
				</SheetHeader>
				<Form {...createmeasurementGroupForm}>
					<form className="p-4 flex flex-col gap-3">
						<FormField
							name="name"
							control={createmeasurementGroupForm.control}
							render={({ field }) => (
								<FormItem>
									<FormLabel>Measurement group name</FormLabel>
									<FormControl>
										<Input
											type="text"
											placeholder="Name of the measurement group"
											onChange={field.onChange}
											onBlur={field.onBlur}
											name={field.name}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>

						<RHFAutoComplete
							control={createmeasurementGroupForm.control}
							name="processObjectList"
							label="Process objects"
							placeholder="Select process objects"
							items={minifiedProcessObjects}
							isMultiSelect
							itemToOption={item => ({
								value: item.id.toString(),
								label: `#${item.id} - ${item.name}`
							})}
						/>

						<FormField
							control={createmeasurementGroupForm.control}
							name="type"
							render={({ field }) => (
								<FormItem>
									<FormLabel>Type</FormLabel>

									<Select
										onValueChange={val => {
											field.onChange(val);
											createmeasurementGroupForm.resetField('objectList');
										}}
										value={field.value ?? ''}
									>
										<FormControl>
											<SelectTrigger className="w-full">
												<SelectValue placeholder="Select measurement group type" />
											</SelectTrigger>
										</FormControl>
										<SelectContent>
											{MeasurementGroupTypes.map(type => (
												<SelectItem key={type} value={type}>
													{type}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
									<FormMessage />
								</FormItem>
							)}
						/>

						{createmeasurementGroupForm.watch('type') === MeasurementGroupTypesEnum.PLANT && (
							<RHFAutoComplete
								control={createmeasurementGroupForm.control}
								name="objectList"
								label="Plants"
								placeholder="Select plants"
								items={mgPlantList}
								isMultiSelect
								itemToOption={item => ({
									value: item.plantId.toString(),
									label: getPlantName(item)
								})}
							/>
						)}

						{createmeasurementGroupForm.watch('type') === MeasurementGroupTypesEnum.LINE && (
							<RHFAutoComplete
								control={createmeasurementGroupForm.control}
								closeOnSelect
								name="objectList"
								label="Lines"
								placeholder="Select line"
								items={mgLineList}
								itemToOption={item => ({
									value: item.pathId.toString(),
									label: item.pathName
								})}
							/>
						)}

						{createmeasurementGroupForm.watch('type') === MeasurementGroupTypesEnum.LOAD && (
							<RHFAutoComplete
								control={createmeasurementGroupForm.control}
								name="objectList"
								label="Loads"
								placeholder="Select loads"
								items={mgLoadList}
								isMultiSelect
								itemToOption={item => ({
									value: item.loadId.toString(),
									label: getLoadName(item)
								})}
							/>
						)}
					</form>
				</Form>

				{error && (
					<div className="text-red-500 text-sm text-center">
						<p>{error}</p>
					</div>
				)}

				<SheetFooter>
					{!showConfirmation ? (
						<Button type="submit" onClick={() => setShowConfirmation(true)}>
							Save changes
						</Button>
					) : (
						<div className="flex flex-col gap-2 text-sm justify-center items-center">
							<p>Are you sure you want to save this measurement group?</p>
							<div className="flex gap-2 w-full justify-center">
								<Button
									className="flex-1"
									variant="secondary"
									onClick={() => {
										setShowConfirmation(false);
										setError('');
									}}
									disabled={isSaving}
								>
									Cancel
								</Button>
								<Button
									className="flex-1"
									type="submit"
									onClick={handleSaveMeasurementGroup}
									disabled={isSaving}
								>
									{error ? 'Try again ' : 'Save'}
									{isSaving && <Spinner className="ml-2" />}
								</Button>
							</div>
						</div>
					)}
				</SheetFooter>
			</SheetContent>
		</Sheet>
	);
};
